import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X, Plus, Trash2, Send, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { api, type AgreementTemplate, type Contact } from "@/lib/api";
import { cn } from "@/lib/utils";

interface Signer {
  name: string;
  email: string;
}

interface Props {
  /** Pre-selected account (skips client picker). */
  accountId?: string;
  accountName?: string;
  /** All loaded templates (parent may already have them). */
  templates?: AgreementTemplate[];
  onClose: () => void;
  onSent: () => void;
}

export function SendForSignatureModal({ accountId: initialAccountId, accountName: initialAccountName, templates: propTemplates, onClose, onSent }: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();

  // Step 1: pick client (skip if accountId provided)
  const [accountId, setAccountId] = useState(initialAccountId ?? "");
  const [accountSearch, setAccountSearch] = useState(initialAccountName ?? "");
  const [accountDropOpen, setAccountDropOpen] = useState(false);

  // Step 2: template + document details
  const [templateId, setTemplateId] = useState("");
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [testMode, setTestMode] = useState(false);

  // Variable values + enabled blocks
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [enabledBlocks, setEnabledBlocks] = useState<Record<string, boolean>>({});

  // Signers
  const [signers, setSigners] = useState<Signer[]>([{ name: "", email: "" }]);

  // Block expansion in UI
  const [blocksExpanded, setBlocksExpanded] = useState(true);

  // Load accounts for search
  const { data: accounts = [] } = useQuery({
    queryKey: ["accounts-list"],
    queryFn: () => api.getAccounts(),
    enabled: !initialAccountId,
  });

  // Load templates if not provided by parent
  const { data: fetchedTemplates = [] } = useQuery({
    queryKey: ["agreement-templates"],
    queryFn: () => api.getAgreementTemplates(),
    enabled: !propTemplates,
  });
  const templates = propTemplates ?? fetchedTemplates;

  // Load contacts for the selected account (to suggest as signers)
  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["contacts", accountId],
    queryFn: () => api.getContacts({ accountId }),
    enabled: !!accountId,
  });

  const selectedTemplate = templates.find((t) => t.id === templateId) ?? null;

  // When template changes, re-initialize variables and blocks
  useEffect(() => {
    if (!selectedTemplate) return;
    const vals: Record<string, string> = {};
    for (const v of selectedTemplate.variables) vals[v.name] = v.default_value ?? "";
    setVariableValues(vals);
    const blocks: Record<string, boolean> = {};
    for (const b of selectedTemplate.optional_blocks) blocks[b.name] = b.default_enabled;
    setEnabledBlocks(blocks);
    if (!title) setTitle(selectedTemplate.name);
  }, [templateId]);

  // Pre-fill first signer from primary contact
  useEffect(() => {
    if (contacts.length > 0 && signers.every((s) => !s.email)) {
      const primary = contacts.find((c) => c.is_primary) ?? contacts[0];
      setSigners([{ name: primary.name ?? "", email: primary.email ?? "" }]);
    }
  }, [contacts]);

  const sendMutation = useMutation({
    mutationFn: () =>
      api.sendForSignature(accountId, {
        template_id: templateId || undefined,
        title,
        signers: signers.filter((s) => s.email.trim()),
        variable_values: variableValues,
        enabled_blocks: Object.entries(enabledBlocks).filter(([, v]) => v).map(([k]) => k),
        subject: subject || undefined,
        message: message || undefined,
        test_mode: testMode,
      }),
    onSuccess: () => {
      toast({ title: "Sent for signature!" });
      qc.invalidateQueries({ queryKey: ["signature-requests"] });
      qc.invalidateQueries({ queryKey: ["account-signature-requests", accountId] });
      onSent();
    },
    onError: (err) => toast({ title: `Send failed: ${String(err)}`, variant: "destructive" }),
  });

  const canSend =
    accountId &&
    title.trim() &&
    signers.some((s) => s.email.trim());

  const filteredAccounts = accountSearch
    ? accounts.filter((a) => a.account_name.toLowerCase().includes(accountSearch.toLowerCase())).slice(0, 10)
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-background z-10">
          <h2 className="font-semibold text-base flex items-center gap-2">
            <Send className="w-4 h-4 text-violet-600" /> Send for Signature
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Client picker */}
          {!initialAccountId && (
            <div className="space-y-1 relative">
              <Label className="text-xs">Client *</Label>
              <Input
                value={accountSearch}
                onChange={(e) => { setAccountSearch(e.target.value); setAccountId(""); setAccountDropOpen(true); }}
                onFocus={() => setAccountDropOpen(true)}
                placeholder="Search client…"
                className="h-8 text-sm"
              />
              {accountDropOpen && filteredAccounts.length > 0 && (
                <div className="absolute z-20 top-full mt-1 w-full bg-background border rounded-md shadow-md max-h-48 overflow-y-auto">
                  {filteredAccounts.map((a) => (
                    <button
                      key={a.id}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                      onClick={() => { setAccountId(a.id); setAccountSearch(a.account_name); setAccountDropOpen(false); }}
                    >
                      {a.account_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Template */}
          <div className="space-y-1">
            <Label className="text-xs">Template</Label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="w-full h-8 text-sm border rounded-md px-2 bg-background"
            >
              <option value="">— No template (ad-hoc) —</option>
              {templates.map((t: AgreementTemplate) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div className="space-y-1">
            <Label className="text-xs">Document Title *</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. 2025 Tax Services Agreement"
              className="h-8 text-sm"
            />
          </div>

          {/* Variable values */}
          {selectedTemplate && selectedTemplate.variables.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs font-medium">Fill in Variables</Label>
              <div className="grid grid-cols-2 gap-2">
                {selectedTemplate.variables.map((v: import("@/lib/api").TemplateVariable) => (
                  <div key={v.name} className="space-y-0.5">
                    <label className="text-xs text-muted-foreground">{v.label}</label>
                    <Input
                      value={variableValues[v.name] ?? ""}
                      onChange={(e) => setVariableValues((prev) => ({ ...prev, [v.name]: e.target.value }))}
                      placeholder={v.default_value || v.name}
                      className="h-7 text-xs"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Optional blocks */}
          {selectedTemplate && selectedTemplate.optional_blocks.length > 0 && (
            <div className="space-y-2">
              <button
                type="button"
                className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setBlocksExpanded(!blocksExpanded)}
              >
                {blocksExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                Optional Sections
              </button>
              {blocksExpanded && (
                <div className="space-y-1.5 pl-1">
                  {selectedTemplate.optional_blocks.map((b: import("@/lib/api").OptionalBlock) => (
                    <label key={b.name} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={enabledBlocks[b.name] ?? b.default_enabled}
                        onChange={(e) => setEnabledBlocks((prev) => ({ ...prev, [b.name]: e.target.checked }))}
                        className="rounded"
                      />
                      <span className="text-xs">{b.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Signers */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Signers *</Label>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 text-xs"
                onClick={() => setSigners((s) => [...s, { name: "", email: "" }])}
              >
                <Plus className="w-3 h-3 mr-1" /> Add signer
              </Button>
            </div>
            {contacts.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {contacts
                  .filter((c) => c.email)
                  .map((c, i) => {
                    const already = signers.some((s) => s.email === c.email);
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          if (already) return;
                          const empty = signers.findIndex((s) => !s.email);
                          if (empty >= 0) {
                            setSigners((prev) => prev.map((s, j) => j === empty ? { name: c.name ?? "", email: c.email ?? "" } : s));
                          } else {
                            setSigners((prev) => [...prev, { name: c.name ?? "", email: c.email ?? "" }]);
                          }
                        }}
                        className={cn(
                          "text-xs px-2 py-0.5 rounded-full border transition-colors",
                          already ? "bg-violet-100 text-violet-700 border-violet-200" : "bg-muted hover:bg-violet-50 text-muted-foreground hover:text-violet-700",
                        )}
                      >
                        {c.name ?? c.email}
                      </button>
                    );
                  })}
              </div>
            )}
            <div className="space-y-1.5">
              {signers.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={s.name}
                    onChange={(e) => setSigners((prev) => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                    placeholder="Signer name"
                    className="h-7 text-xs flex-1"
                  />
                  <Input
                    value={s.email}
                    onChange={(e) => setSigners((prev) => prev.map((x, j) => j === i ? { ...x, email: e.target.value } : x))}
                    placeholder="Signer email"
                    className="h-7 text-xs flex-1"
                    type="email"
                  />
                  {signers.length > 1 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-rose-600"
                      onClick={() => setSigners((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Email subject + message (collapsible) */}
          <details className="space-y-2">
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Customize email subject & message</summary>
            <div className="space-y-2 pt-2">
              <div className="space-y-1">
                <Label className="text-xs">Email Subject</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={`Please sign: ${title || "Agreement"}`}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Message to Signer</Label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Please review and sign the attached agreement."
                  className="text-sm min-h-[80px] resize-none"
                  rows={3}
                />
              </div>
            </div>
          </details>

          {/* Test mode */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={testMode}
              onChange={(e) => setTestMode(e.target.checked)}
              className="rounded"
            />
            <span className="text-xs text-muted-foreground">
              Test mode — document won't be legally binding (for setup/testing only)
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-5 border-t sticky bottom-0 bg-background">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => sendMutation.mutate()}
            disabled={!canSend || sendMutation.isPending}
            className="gap-1.5"
          >
            <Send className="w-4 h-4" />
            {sendMutation.isPending ? "Sending…" : "Send for Signature"}
          </Button>
        </div>
      </div>
    </div>
  );
}
